/**
 * components/PenyesuaianModal.js
 * Adjust ONE bucket's balance directly (the common path: 17 -> 15). The new value is
 * typed in; the app computes the delta and logs a PENYESUAIAN riwayat entry.
 */
import React, { useEffect, useState } from "react";
import { fmtQty } from "../utils/idGenerators";
import { bucketLabel } from "../utils/transformations";
import { useModalEsc } from "../utils/modalStack";
import ContactPicker from "./ContactPicker";

export default function PenyesuaianModal({ item, contacts = [], onCreateContact, onSubmit, onClose }) {
  // Internal contacts are app-maintained TRANSFER stand-ins, not real shops — never offer one
  // as a pickable adjustment source (Contact model redesign leak fix). A deactivated contact is
  // excluded the same way (Contact model redesign, stage 2) — `contactId` here always starts
  // `null`, never preloaded from a persisted value, so there's no "already selected" case to
  // preserve.
  // List-scoping (KontakPage batch 2b): unlike ItemFormModal (see that file's own comment),
  // this modal genuinely has a location anchor — `bucket` IS "PUSAT"|"CABANG", the exact same
  // two values a Contact's own `list` uses, and exactly one bucket is adjusted per save. So the
  // picker only ever offers a contact from the bucket currently being adjusted.
  const [bucket, setBucket] = useState("PUSAT");
  const pickableContacts = contacts.filter((c) => !c.internal && !c.deactivated && c.list === bucket);
  const [note, setNote] = useState("");
  const [contactId, setContactId] = useState(null);
  // Bucket whose unsaved typed edit was just discarded by a bucket switch, or null. Purely
  // presentational (see switchBucket below) — never affects the reset itself or the payload.
  const [discardedBucket, setDiscardedBucket] = useState(null);

  const current = bucket === "CABANG" ? item.stokCabang : item.stokPusat;
  const [target, setTarget] = useState(String(current));

  // Reset the target input whenever the selected bucket changes. Unchanged behavior — only
  // made VISIBLE (see switchBucket / the warning banner below), never altered.
  useEffect(() => { setTarget(String(bucket === "CABANG" ? item.stokCabang : item.stokPusat)); }, [bucket, item]);

  // Drive-by: was a hand-rolled keydown effect predating the modal-stack convention.
  useModalEsc(onClose);

  const newVal = Number(target) || 0;
  const delta = newVal - (Number(current) || 0);

  // Switching buckets always resets the typed value (via the effect above) — this only makes
  // that reset visible: if there was an unsaved edit (delta !== 0) for the bucket being left,
  // remember it so a warning can name it; otherwise clear any stale warning.
  // Also clears any picked contact (List-scoping, batch 2b): a contact picked for the bucket
  // being left belongs to that bucket's own list and stops being a valid pick for the new one —
  // keeping it selected here would silently hold an id `pickableContacts` no longer offers.
  const switchBucket = (next) => {
    if (next === bucket) return;
    setDiscardedBucket(delta !== 0 ? bucket : null);
    setBucket(next);
    setContactId(null);
  };

  const submit = (e) => {
    e.preventDefault();
    setDiscardedBucket(null);
    if (delta === 0) return;
    onSubmit({ bucket, newValue: newVal, delta, note: note.trim(), contactId: contactId || null });
  };

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true">
      <form className="modal-box" onSubmit={submit}>
        <h2 className="modal-title">Penyesuaian Stok</h2>
        <p className="page-sub" style={{ marginBottom: "var(--gap-lg)" }}>{item.name} · <span className="mono">{item.internalCode}</span></p>

        <div className="form-row">
          <label className="form-label">Bucket</label>
          <div className="seg">
            <button type="button" className={bucket === "PUSAT" ? "active" : ""} onClick={() => switchBucket("PUSAT")}>Pusat</button>
            <button type="button" className={bucket === "CABANG" ? "active" : ""} onClick={() => switchBucket("CABANG")}>Cabang</button>
          </div>
          <p className="form-hint">Penyesuaian disimpan untuk satu bucket saja per simpan.</p>
        </div>

        {discardedBucket && (
          <div className="alert" style={{ background: "#fef3c7", color: "#92400e", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "var(--gap-md)" }}>
            <span>Perubahan Stok {bucketLabel(discardedBucket)} yang belum disimpan dibuang saat pindah bucket.</span>
            <button type="button" className="btn btn-ghost btn-sm" style={{ color: "#92400e" }}
              onClick={() => setDiscardedBucket(null)} aria-label="Tutup peringatan">×</button>
          </div>
        )}

        <div className="form-grid">
          <div className="form-row">
            <label className="form-label" htmlFor="penyesuaian-stok-sekarang">Stok {bucketLabel(bucket)} sekarang</label>
            <input id="penyesuaian-stok-sekarang" className="form-input" value={fmtQty(current)} readOnly />
          </div>
          <div className="form-row">
            <label className="form-label" htmlFor="penyesuaian-stok-baru">Stok baru</label>
            <input id="penyesuaian-stok-baru" className="form-input" type="number" value={target} autoFocus
              onChange={(e) => { setTarget(e.target.value); setDiscardedBucket(null); }} />
          </div>
        </div>

        <div className={`alert ${delta === 0 ? "alert--info" : "alert--ok"}`}>
          Perubahan: <b>{delta > 0 ? `+${delta}` : delta}</b>
          {delta !== 0 ? ` (akan dicatat sebagai PENYESUAIAN di riwayat)` : " (tidak ada perubahan)"}
        </div>

        <div className="form-row">
          <label className="form-label" htmlFor="penyesuaian-asal-stok">Asal Stok (opsional)</label>
          <ContactPicker id="penyesuaian-asal-stok" value={contactId} contacts={pickableContacts}
            onChange={setContactId} onCreateContact={onCreateContact} />
        </div>

        <div className="form-row">
          <label className="form-label" htmlFor="penyesuaian-catatan">Catatan (opsional)</label>
          <input id="penyesuaian-catatan" className="form-input" value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="cth. koreksi hitung fisik" />
        </div>

        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose}>Batal</button>
          <button type="submit" className="btn btn-primary" disabled={delta === 0}>Simpan Penyesuaian</button>
        </div>
      </form>
    </div>
  );
}
